import { enviroment } from "../../enviroment";
import { parseMethodSizeLimit } from "./SizeUtil";

// Flag REACT_APP_LOGO_METHOD_MAX_SIZE_BY_ORG_ENABLED (paired with the API's
// LogoMethodMaxSizeByOrgEnabled): selected customers can have their own max
// logo size per transfer method. The API owns the values and resolves them
// in its ONE policy; the FE has no limits of its own. What the FE gets:
//  - logo.transferMethod.size = the limit of the logo's CURRENT method for
//    THIS logo (the customer's own or inherited value, else the general one)
//    - as before, now resolved per logo;
//  - logo.methodMaxSizes = [ { typeAndKey, maxSize } ] - only the methods
//    the logo's customer has a customer-specific value for (typeAndKey
//    trimmed + upper-cased). Left OUT of the JSON when the API's flag is off
//    or the customer has no override, and never sent in list rows (detail
//    and mutation answers only).
// The method list (GET logos/options/all, cache key ["logo-options"]) is
// GENERAL and never carries a customer's value. So when ANOTHER method is
// picked for a logo, its limit is
//   logo.methodMaxSizes[normalize(method.typeAndKey)] cut to method.size
// per side ("only smaller", Nichlas 30/9: a customer's limit is never
// larger than the general one; the API cuts the list to the admin / setting
// value, and the method list's size is the general limit of a method
// without one, so the FE cuts to that as well - clipToMethodSize), else
// method.size
// - and the SAME rule gives the ONE effective limit of the form's CURRENT
// method (formMethodWithLogoMaxSize), which every clip and cap in the
// editors reads: a logo opened from a LIST row carries the row's
// transferMethod.size, which the API resolves without the customer in
// some flows (admin list without a customer organisation, logos in design
// rows, a vendor on another route organisation), so the row's size may be
// the general one until the logo's first detail / mutation answer lands
// with the list and the effective size. That is all this file does.
//
// The method objects the editor's dropdown hands over are the very objects
// of the shared ["logo-options"] cache (30 min, every logo / customer /
// act-as identity reads it). methodWithLogoMaxSize therefore ALWAYS returns
// a copy and never writes to its input - a customer's limit written into
// that object would follow into the next logo. Flag off = every helper says
// "no customer values" and the method is returned as given (same
// reference): byte-identical behaviour.

export interface LogoMethodMaxSizeEntry {
  /** The method's identity, trimmed + upper-cased (e.g. "TRANSFER_STANDARD"). */
  typeAndKey: string;
  /** The limit for this logo, "AxB" in whole mm. */
  maxSize: string;
}

/** Whatever carries the list: a logo, the raw DTO or the editor's form. */
export type LogoMethodMaxSizeSource =
  | { methodMaxSizes?: LogoMethodMaxSizeEntry[] | null }
  | null
  | undefined;

/** The API's own key comparison: trimmed + upper-cased. */
export function normalizeMethodKey(typeAndKey: string | null | undefined): string {
  return (typeAndKey ?? "").trim().toUpperCase();
}

// "AxB" with two positive whole numbers - the only shape the editors' fit
// (fitSizeToTransferMethod) reads as a limit. Anything else is ignored so a
// malformed entry can never widen or remove the general limit.
function isUsableSize(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const match = /^(\d{1,5})x(\d{1,5})$/.exec(value.trim());
  return !!match && Number(match[1]) > 0 && Number(match[2]) > 0;
}

/**
 * The customer-specific limits a logo answer carries, or null: flag off, no
 * source, no list, an empty list. Entries without a key or without a usable
 * size are dropped.
 */
export function readLogoMethodMaxSizes(
  source: unknown,
): LogoMethodMaxSizeEntry[] | null {
  if (!enviroment.logoMethodMaxSizeByOrgEnabled) return null;
  if (source === null || typeof source !== "object") return null;
  const raw = (source as { methodMaxSizes?: unknown }).methodMaxSizes;
  if (!Array.isArray(raw)) return null;
  const entries: LogoMethodMaxSizeEntry[] = [];
  for (const item of raw) {
    if (item === null || typeof item !== "object") continue;
    const entry = item as Partial<LogoMethodMaxSizeEntry>;
    if (typeof entry.typeAndKey !== "string") continue;
    const key = normalizeMethodKey(entry.typeAndKey);
    if (key === "" || !isUsableSize(entry.maxSize)) continue;
    entries.push({ typeAndKey: key, maxSize: entry.maxSize.trim() });
  }
  return entries.length > 0 ? entries : null;
}

/**
 * The limit THIS logo's customer has for the method, or null when the
 * customer has none for it (= the method list's size applies).
 */
export function logoMaxSizeForMethod(
  source: LogoMethodMaxSizeSource,
  typeAndKey: string | null | undefined,
): string | null {
  const entries = readLogoMethodMaxSizes(source);
  if (!entries) return null;
  const key = normalizeMethodKey(typeAndKey);
  if (key === "") return null;
  return entries.find((entry) => entry.typeAndKey === key)?.maxSize ?? null;
}

/**
 * The customer's limit cut to the method's size per side - "only smaller":
 * the API clips a customer's value to the general max size (the admin
 * value or the setting) and the logo answer's list to THAT, not to the
 * list's own size of a method without one. The general policy reads the
 * list's size as that method's limit, so an older row above it (saved
 * before the rule) must not widen it here. Orientation-independent like the
 * API's MethodMaxSizeLimit.ClipTo: the smaller short side and the smaller
 * long side, written in the customer value's own order (350x450 -> 300x400,
 * 250x500 -> 250x400, 500x250 -> 400x250 against 300x400); the value itself
 * when it is within (equal is within) or when the method's size is not a
 * usable limit. Whole mm, read as the editors' fit reads a limit
 * (parseMethodSizeLimit: the integer part of each side).
 */
export function clipToMethodSize(customerSize: string, methodSize: string): string {
  const ceiling = parseMethodSizeLimit(methodSize);
  const value = parseMethodSizeLimit(customerSize);
  if (!ceiling || !value) return customerSize;
  if (value.shortMm <= ceiling.shortMm && value.longMm <= ceiling.longMm) {
    return customerSize;
  }
  const shortMm = Math.min(value.shortMm, ceiling.shortMm);
  const longMm = Math.min(value.longMm, ceiling.longMm);
  const [first, second] = customerSize.split("x").map((side) => Math.floor(Number(side)));
  return first <= second ? `${shortMm}x${longMm}` : `${longMm}x${shortMm}`;
}

/**
 * The method as it applies to THIS logo: its `size` is the customer's limit
 * when the logo's answer carries one for the method - cut to the size the
 * method came with per side (clipToMethodSize; "only smaller") -, else that
 * size (the method list's general one). With the flag on the result is
 * ALWAYS a new object - the input (an object of the shared ["logo-options"]
 * cache) is never written to. Flag off = the method itself.
 */
export function methodWithLogoMaxSize<
  T extends { typeAndKey: string; size: string },
>(source: LogoMethodMaxSizeSource, method: T): T {
  if (!enviroment.logoMethodMaxSizeByOrgEnabled) return method;
  const own = logoMaxSizeForMethod(source, method.typeAndKey);
  const size = own === null ? method.size : clipToMethodSize(own, method.size);
  return { ...method, size };
}

/**
 * Where the editor may read the customer's limits from: the form (it holds
 * the list of the logo it was built from) - but ONLY while the form still
 * has the customer the saved logo has. The list belongs to that customer;
 * once another customer is picked in the form (not saved yet) it says
 * nothing about the new one, so the method list's general size applies
 * until the save answers with the new customer's own list. null = no
 * customer values.
 */
export function logoMaxSizeSourceOfForm(
  form:
    | {
        customer?: { id?: number } | null;
        methodMaxSizes?: LogoMethodMaxSizeEntry[] | null;
      }
    | null
    | undefined,
  saved: { customer?: { id?: number } | null } | null | undefined,
): LogoMethodMaxSizeSource {
  if (!enviroment.logoMethodMaxSizeByOrgEnabled) return null;
  if (!form) return null;
  const formCustomerId = form.customer?.id ?? null;
  const savedCustomerId = saved?.customer?.id ?? null;
  if (formCustomerId !== savedCustomerId) return null;
  return form;
}

/**
 * The editor form's share of a logo answer: { methodMaxSizes } (the
 * answer's own list) when it carries customer values, else {} - so with the
 * flag off, and for a logo without them, the form has exactly the keys it
 * has today. Spread into BOTH toLogoForm (standalone editor and design
 * editor).
 */
export function logoMethodMaxSizesFormPart(
  logo: LogoMethodMaxSizeSource,
): { methodMaxSizes?: LogoMethodMaxSizeEntry[] } {
  if (!readLogoMethodMaxSizes(logo)) return {};
  return { methodMaxSizes: logo!.methodMaxSizes! };
}

/**
 * The editor form after a logo answer that is NOT run through toLogoForm
 * (the design editor's resize answers only copy the size into the form):
 * the form takes the answer's customer values (or loses its own when the
 * answer has none), and - when the form still shows the answer's method -
 * the limit the answer carries for it (transferMethod.size is the API's
 * effective limit for this logo). Everything the user may be editing is
 * kept. Always a new object with the flag on; flag off = the form itself.
 */
export function formWithAnswerMaxSizes<
  F extends {
    transferMethod?: { key?: string; size?: string } | null;
    methodMaxSizes?: LogoMethodMaxSizeEntry[] | null;
  },
>(
  form: F,
  answer:
    | {
        transferMethod?: { key?: string; size?: string } | null;
        methodMaxSizes?: LogoMethodMaxSizeEntry[] | null;
      }
    | null
    | undefined,
): F {
  if (!enviroment.logoMethodMaxSizeByOrgEnabled) return form;
  if (!answer) return form;
  const rest: F = { ...form };
  delete rest.methodMaxSizes;
  const nextMethod = methodWithAnswerSize(
    form.transferMethod,
    answer.transferMethod,
  );
  return {
    ...rest,
    ...(nextMethod !== form.transferMethod ? { transferMethod: nextMethod } : {}),
    ...logoMethodMaxSizesFormPart(answer),
  } as F;
}

/**
 * The form's method after a logo ANSWER (detail, resize, update) for the
 * SAME method (same key): a copy bearing the answer's `transferMethod.size`
 * - the API's effective limit of that method for THIS logo - when it is
 * usable and differs from the form's. The same reference when nothing
 * differs, when the answer carries no usable size, or when the answer's
 * method is ANOTHER one (an unsaved method change in the form: the answer
 * says nothing about the picked method). Flag off = the form's method.
 */
export function methodWithAnswerSize<
  M extends { key?: string; size?: string } | null | undefined,
>(
  formMethod: M,
  answerMethod: { key?: string; size?: string } | null | undefined,
): M {
  if (!enviroment.logoMethodMaxSizeByOrgEnabled) return formMethod;
  if (!formMethod || !answerMethod) return formMethod;
  if (typeof formMethod.key !== "string" || formMethod.key !== answerMethod.key) {
    return formMethod;
  }
  const answerSize = answerMethod.size;
  if (typeof answerSize !== "string" || answerSize === "") return formMethod;
  if (answerSize === formMethod.size) return formMethod;
  return { ...formMethod, size: answerSize } as M;
}

/**
 * The form's CURRENT method as it applies to THIS logo - the ONE effective
 * limit every clip and cap in the editors reads (the size fields, the
 * corner drag and "Change size" of LogoEditorTool): its `size` is the
 * customer's value for the method when the form holds one (methodMaxSizes,
 * the API's resolution for this logo from its detail / mutation answer, and
 * only while the form still has the saved logo's customer -
 * logoMaxSizeSourceOfForm), else the size the method came with (the
 * answer's own effective size, or a list row's / the method list's general
 * one). Always a copy with the flag on (the form's object - possibly one of
 * the shared ["logo-options"] cache - is never written to); a form without
 * a method gives what it holds. Flag off = the form's method itself.
 */
export function formMethodWithLogoMaxSize<
  T extends { typeAndKey: string; size: string },
>(
  form:
    | {
        transferMethod?: T | null;
        customer?: { id?: number } | null;
        methodMaxSizes?: LogoMethodMaxSizeEntry[] | null;
      }
    | null
    | undefined,
  saved: { customer?: { id?: number } | null } | null | undefined,
): T | null | undefined {
  const method = form?.transferMethod;
  if (!enviroment.logoMethodMaxSizeByOrgEnabled) return method;
  if (!method) return method;
  return methodWithLogoMaxSize(logoMaxSizeSourceOfForm(form, saved), method);
}

/** True when the two lists hold the same entries in the same order. */
export function sameLogoMethodMaxSizes(
  a: LogoMethodMaxSizeEntry[] | null | undefined,
  b: LogoMethodMaxSizeEntry[] | null | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return (a?.length ?? 0) === 0 && (b?.length ?? 0) === 0;
  if (a.length !== b.length) return false;
  return a.every(
    (entry, index) =>
      normalizeMethodKey(entry?.typeAndKey) ===
        normalizeMethodKey(b[index]?.typeAndKey) &&
      entry?.maxSize === b[index]?.maxSize,
  );
}
